import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Korniza e dialogjeve të faqes prezantuese (demonstrimi, regjistrimi).
 *
 * Esc dhe klikimi jashtë e mbyllin; Tab rri brenda; faqja pas tij nuk rrëshqet.
 * Fokusi i parë shkon te `[data-autofocus]`, ose te fusha e parë. Kur ndërrohet
 * pamja brenda (hapi 2, «u dërgua»), jepi `focusKey` të ri që fokusi të rivendoset.
 */

const FOCUSABLE = 'button:not([disabled]), input:not([tabindex="-1"]):not([disabled]), textarea, select, a[href]';

export default function Dialog({ labelledBy, describedBy, closeLabel, onClose, wide = false, focusKey, children }) {
  const ref = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const first = root.querySelector('[data-autofocus]')
      || root.querySelector('input:not([tabindex="-1"]), textarea, select')
      || root.querySelector(FOCUSABLE);
    first && first.focus();
  }, [focusKey]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = ref.current.querySelectorAll(FOCUSABLE);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    <div className="nxp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        className={`nxp-dialog${wide ? ' is-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onKeyDown={onKeyDown}
      >
        <button type="button" className="nxp-dialog-close" onClick={onClose} aria-label={closeLabel}>
          <X size={18} aria-hidden="true" />
        </button>
        {children}
      </div>
    </div>
  );
}
